import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Check, X } from 'lucide-react';

export default function SecuritySetupStep({ formData, onChange, termsPath = '/terms-of-service' }) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password strength calculation
  const calculatePasswordStrength = (password) => {
    if (!password) return { score: 0, label: '', color: '' };
    
    let score = 0;
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };

    // Calculate score
    Object.values(checks).forEach(check => {
      if (check) score++;
    });

    // Determine strength level
    if (score <= 2) return { score, label: 'Weak', color: 'text-red-500', bgColor: 'bg-red-100' };
    if (score <= 3) return { score, label: 'Fair', color: 'text-orange-500', bgColor: 'bg-orange-100' };
    if (score <= 4) return { score, label: 'Good', color: 'text-yellow-500', bgColor: 'bg-yellow-100' };
    return { score, label: 'Strong', color: 'text-green-500', bgColor: 'bg-green-100' };
  };

  const passwordStrength = calculatePasswordStrength(formData.password);
  const isPasswordMatch = formData.password && formData.password_confirmation && 
                         formData.password === formData.password_confirmation;

  // Password requirements
  const requirements = [
    { label: 'At least 8 characters', met: formData.password?.length >= 8 },
    { label: 'Contains lowercase letter', met: /[a-z]/.test(formData.password || '') },
    { label: 'Contains uppercase letter', met: /[A-Z]/.test(formData.password || '') },
    { label: 'Contains number', met: /\d/.test(formData.password || '') },
    { label: 'Contains special character', met: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password || '') },
  ];

  return (
    <div className="space-y-4">
      {/* Password Field */}
      <div className="space-y-2">
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="Password"
            value={formData.password}
            onChange={(e) => onChange('password', e.target.value)}
            className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 outline-none bg-white/95 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 pr-12"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>

        {/* Password Strength Indicator */}
        {formData.password && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-2"
          >
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${
                    passwordStrength.score <= 2 ? 'bg-red-500' :
                    passwordStrength.score <= 3 ? 'bg-orange-500' :
                    passwordStrength.score <= 4 ? 'bg-yellow-500' : 'bg-green-500'
                  }`}
                  style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                />
              </div>
              <span className={`text-xs font-medium ${passwordStrength.color}`}>
                {passwordStrength.label}
              </span>
            </div>

            {/* Password Requirements */}
            <div className="space-y-1">
              {requirements.map((req, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center gap-2 text-xs"
                >
                  {req.met ? (
                    <Check size={14} className="text-green-500" />
                  ) : (
                    <X size={14} className="text-gray-300" />
                  )}
                  <span className={req.met ? 'text-green-600' : 'text-gray-500'}>
                    {req.label}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* Confirm Password Field */}
      <div className="space-y-2">
        <div className="relative">
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Confirm Password"
            value={formData.password_confirmation}
            onChange={(e) => onChange('password_confirmation', e.target.value)}
            className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 outline-none bg-white/95 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 pr-12"
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>

        {/* Password Match Indicator */}
        {formData.password_confirmation && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 text-xs"
          >
            {isPasswordMatch ? (
              <>
                <Check size={14} className="text-green-500" />
                <span className="text-green-600">Passwords match</span>
              </>
            ) : (
              <>
                <X size={14} className="text-red-500" />
                <span className="text-red-500">Passwords do not match</span>
              </>
            )}
          </motion.div>
        )}
      </div>

      {/* Terms and Conditions */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="flex items-start gap-3 pt-4"
      >
        <label className="flex items-start gap-3 cursor-pointer group">
          <input
            type="checkbox"
            checked={formData.acceptedTerms || false}
            onChange={(e) => onChange('acceptedTerms', e.target.checked)}
            className="mt-1 w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 focus:ring-2 accent-emerald-500 cursor-pointer"
          />
          <span className="text-sm text-gray-600">
            I have read and accepted the{' '}
            <Link
              to={termsPath}
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-600 font-medium underline underline-offset-2 hover:text-emerald-700"
            >
              Terms and Conditions
            </Link>
          </span>
        </label>
      </motion.div>
    </div>
  );
}


